/**
 * The Reach Wire HUD ticker (M4 spec §15 item 1): a strip at the top right. A new item slides in and stays for
 * `showS` (8 s); items arriving meanwhile wait, at most `maxQueued` (3; the oldest waiting one drops). Severity
 * sets the accent and a glyph.
 *
 * - `createTickerQueue`: the pure queue. `push` runs on Wire events; `step(dt)` runs every frame and allocates
 *   nothing (a fixed ring of slots).
 * - `createTickerView`: the DOM strip over cached elements. `sync` writes only `transform` and `textContent`
 *   (the M1 HUD rule), and nothing at all when they are unchanged; the severity attribute changes only with the item.
 *   H6: the meta line names the item's system (never its raw id) and the cause line is empty when the headline
 *   already says it.
 */
import { z } from 'zod'
import type { WireItem } from '../../strategic/types'
import { causeLine, SEVERITY_GLYPHS, SEVERITY_LABELS, wireSystemName, type SystemNameFn } from '../../ui/strategic/wireModel'
import wireUiJson from './wire-ui.json'

export const wireUiSchema = z.object({
  comment: z.string().optional(),
  ticker: z.object({
    showS: z.number().positive(),
    maxQueued: z.number().int().min(0).max(16),
    slideS: z.number().min(0),
    offsetPx: z.number().positive(),
  }),
  /** Intel › Wire in the dashboard (W3 card M1). */
  dashboard: z.object({
    label: z.string().min(1),
    order: z.number(),
    badgeMinSeverity: z.number().int().min(1).max(3),
  }),
})
export type WireUiConfig = z.infer<typeof wireUiSchema>
export type TickerConfig = WireUiConfig['ticker']

export function loadWireUiConfig(): WireUiConfig {
  return wireUiSchema.parse(wireUiJson)
}

export interface TickerQueue {
  /** The item on the strip, or null. */
  readonly current: WireItem | null
  /** Seconds the current item has shown. */
  readonly shownS: number
  /** Items waiting. */
  readonly queued: number
  /** Increments whenever `current` changes. */
  readonly version: number
  push(item: WireItem): void
  /** Advances the clock; the next item takes the strip when the current one's time is up. Allocation-free. */
  step(dt: number): void
  clear(): void
}

export function createTickerQueue(cfg: TickerConfig): TickerQueue {
  const cap = cfg.maxQueued
  const ring: (WireItem | null)[] = new Array<WireItem | null>(Math.max(1, cap)).fill(null)
  let head = 0
  let count = 0
  let current: WireItem | null = null
  let shownS = 0
  let version = 0

  function take(): WireItem | null {
    if (count === 0) return null
    const item = ring[head]
    ring[head] = null
    head = (head + 1) % ring.length
    count--
    return item
  }

  return {
    get current(): WireItem | null {
      return current
    },
    get shownS(): number {
      return shownS
    },
    get queued(): number {
      return count
    },
    get version(): number {
      return version
    },
    push(item: WireItem): void {
      if (current === null) {
        current = item
        shownS = 0
        version++
        return
      }
      if (cap === 0) return
      if (count === cap) take()
      ring[(head + count) % ring.length] = item
      count++
    },
    step(dt: number): void {
      if (current === null) return
      shownS += dt
      if (shownS < cfg.showS) return
      current = take()
      shownS = 0
      version++
    },
    clear(): void {
      for (let i = 0; i < ring.length; i++) ring[i] = null
      head = 0
      count = 0
      if (current !== null) version++
      current = null
      shownS = 0
    },
  }
}

/** The strip's horizontal offset in px at `shownS` of `showS` (slides in, holds, slides out; hidden when null). */
export function tickerOffset(cfg: TickerConfig, shownS: number, visible: boolean): number {
  if (!visible) return cfg.offsetPx
  if (cfg.slideS <= 0) return 0
  const inT = Math.min(1, shownS / cfg.slideS)
  const outT = Math.min(1, Math.max(0, (cfg.showS - shownS) / cfg.slideS))
  const t = Math.min(inT, outT)
  // Ease out: fast in, soft landing.
  const e = 1 - (1 - t) * (1 - t)
  return Math.round((1 - e) * cfg.offsetPx)
}

/** The elements the view writes (real DOM in the game, fakes in tests). */
export interface TickerElements {
  root: { style: { transform: string }; dataset: Record<string, string | undefined> }
  meta: { textContent: string | null }
  headline: { textContent: string | null }
  cause: { textContent: string | null }
}

export interface TickerView {
  /** Writes the queue's state into the elements; writes nothing when nothing changed. Allocation-free when unchanged. */
  sync(q: TickerQueue): void
  dispose(): void
}

export function createTickerView(els: TickerElements, cfg: TickerConfig, dispose: () => void = () => {}, systemName?: SystemNameFn): TickerView {
  let shownVersion = -1
  let lastOffset = Number.NaN
  // One transform string per whole pixel, built once, so a slide writes without allocating.
  const max = Math.max(0, Math.ceil(cfg.offsetPx))
  const transforms: string[] = []
  for (let px = 0; px <= max; px++) transforms.push(`translateX(${px}px)`)
  return {
    sync(q: TickerQueue): void {
      const item = q.current
      if (q.version !== shownVersion) {
        shownVersion = q.version
        if (item !== null) {
          els.root.dataset.severity = String(item.severity)
          const system = wireSystemName(item.systemId, systemName)
          const where = system === null ? '' : ` · ${system}`
          els.meta.textContent = `${SEVERITY_GLYPHS[item.severity]} ${SEVERITY_LABELS[item.severity]} · Reach Wire · Day ${Math.max(0, Math.floor(item.day))}${where}`
          els.headline.textContent = item.headline
          els.cause.textContent = causeLine(item) ?? ''
        }
      }
      const off = tickerOffset(cfg, q.shownS, item !== null)
      if (off !== lastOffset) {
        lastOffset = off
        els.root.style.transform = transforms[Math.min(max, Math.max(0, off))]
      }
    },
    dispose,
  }
}

/** Builds the strip in `parent` (the HUD layer) and its view; `systemName` names the item's system (H6). */
export function createDomTicker(parent: HTMLElement, cfg: TickerConfig, systemName?: SystemNameFn): TickerView {
  const doc = parent.ownerDocument
  const root = doc.createElement('div')
  root.className = 'wire-ticker'
  root.setAttribute('role', 'status')
  root.setAttribute('aria-live', 'polite')
  root.dataset.testid = 'wire-ticker'
  const meta = doc.createElement('span')
  meta.className = 'wire-ticker__meta'
  const headline = doc.createElement('span')
  headline.className = 'wire-ticker__headline'
  const cause = doc.createElement('span')
  cause.className = 'wire-ticker__cause'
  root.append(meta, headline, cause)
  root.style.transform = `translateX(${cfg.offsetPx}px)`
  parent.appendChild(root)
  return createTickerView({ root, meta, headline, cause }, cfg, () => root.remove(), systemName)
}
